import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { ensureAvailability } from '../../../../../store/reconciliations/reconciliationsSlice';
import { selectAvailability } from '../../../../../store/reconciliations/selectors';
import { DATA_AVAILABILITY_WINDOW_LAST_12M, PERIOD_IDS, RECENT_FY_OPTION_COUNT } from '../../../../../store/reconciliations/constants';
import { getCurrentFinancialYearEndYear, getRecentFinancialYearStartYears } from '../../../../../utils/dateUtils';
import { DATA_AVAILABILITY_COPY as COPY } from '../constants/reconciliationsCopy';
import type { DataAvailability, DataAvailabilityWindow } from '../../../../../store/reconciliations/types';

interface Period {
    fy: number | null;
    window: DataAvailabilityWindow | null;
}

/** Drives the modal's data: one fetch per (org, period), cached in redux; it opens on the current FY. */
export function useDataAvailabilityLogic(orgId: string) {
    const dispatch = useAppDispatch();
    const [requested, setRequested] = useState<Period>(() => ({ fy: getCurrentFinancialYearEndYear() - 1, window: null }));
    const entry = useAppSelector((state) => selectAvailability(state, orgId, requested.fy, requested.window));

    // Last good payload for this org, kept so the grid stays mounted (and the dialog the same height) while a new period loads.
    const lastDataRef = useRef<{ orgId: string; data: DataAvailability } | null>(null);
    useEffect(() => {
        if (entry?.status === 'success' && entry.data) lastDataRef.current = { orgId, data: entry.data };
    }, [entry, orgId]);

    const requestPeriod = useCallback(() => {
        dispatch(ensureAvailability({ orgId, fy: requested.fy, window: requested.window }));
    }, [dispatch, orgId, requested]);

    useEffect(() => {
        requestPeriod();
    }, [requestPeriod]);

    const selectFy = useCallback((fy: number) => setRequested({ fy, window: null }), []);
    const selectLast12Months = useCallback(() => setRequested({ fy: null, window: DATA_AVAILABILITY_WINDOW_LAST_12M }), []);

    // The period the server echoes back, falling back to the one requested while loading.
    const shownPeriod: Period = entry?.data ? { fy: entry.data.fy, window: entry.data.window } : requested;

    const fyOptions = getRecentFinancialYearStartYears(RECENT_FY_OPTION_COUNT);
    const currentFy = fyOptions[fyOptions.length - 1];
    const lastFy = currentFy - 1;
    const periodOptions = [
        { id: PERIOD_IDS.LAST_FY, label: COPY.LAST_FY },
        { id: PERIOD_IDS.LAST_12M, label: COPY.LAST_12_MONTHS },
        { id: PERIOD_IDS.CURRENT_FY, label: COPY.CURRENT_FY },
    ];
    let selectedPeriodId: string | null = null;
    if (shownPeriod.window !== null) selectedPeriodId = PERIOD_IDS.LAST_12M;
    else if (shownPeriod.fy === currentFy) selectedPeriodId = PERIOD_IDS.CURRENT_FY;
    else if (shownPeriod.fy === lastFy) selectedPeriodId = PERIOD_IDS.LAST_FY;
    const selectPeriod = (id: string) => {
        if (id === PERIOD_IDS.LAST_12M) selectLast12Months();
        else selectFy(id === PERIOD_IDS.CURRENT_FY ? currentFy : lastFy);
    };

    return {
        entry,
        lastData: lastDataRef.current?.orgId === orgId ? lastDataRef.current.data : null,
        shownPeriod,
        fyOptions,
        periodOptions,
        selectedPeriodId,
        selectFy,
        selectPeriod,
        retry: requestPeriod,
    };
}
